import * as React from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'

import remarkGfm from 'remark-gfm'

import { Heading, Link, Text } from '@grammarly/design-system'

import classes from './GDSMarkdown.module.scss'
import { CodeBlock } from './CodeBlock'

type Props = {
  readonly children: string
  readonly updateComponents?: (defaultComponentMap: Components) => Components
}

const headerProps = {
  variant: 'heading-xsmall' as const,
  className: classes.gdsMarkdownHeading,
}

const defaultComponentMap: Components = {
  p: ({ children }) => (
    <Text as="p" variant="text-small" className={classes.gdsMarkdownParagraph}>
      {children}
    </Text>
  ),
  // use p for blockquote
  blockquote: ({ children }) => (
    <Text as="p" variant="text-small" className={classes.gdsMarkdownParagraph}>
      {children}
    </Text>
  ),
  h1: ({ children }) => (
    <Heading as="h1" {...headerProps}>
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading as="h2" {...headerProps}>
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading as="h3" {...headerProps}>
      {children}
    </Heading>
  ),
  h4: ({ children }) => (
    <Heading as="h4" {...headerProps}>
      {children}
    </Heading>
  ),
  h5: ({ children }) => (
    <Heading as="h5" {...headerProps}>
      {children}
    </Heading>
  ),
  h6: ({ children }) => (
    <Heading as="h6" {...headerProps}>
      {children}
    </Heading>
  ),
  code: props => {
    const { children, inline, className } = props

    if (inline) {
      return <code>{children}</code>
    }

    const match = /language-(\w+)/.exec(className || '')
    const language = match ? match[1] : undefined

    const maybeStringValue =
      Array.isArray(children) && children.length === 1 ? children[0] : children

    if (!language || typeof maybeStringValue !== 'string') {
      return (
        <pre>
          <code>{children}</code>
        </pre>
      )
    }

    return <CodeBlock value={maybeStringValue} language={language} />
  },
  a: ({ children, href }) => (
    <Link href={href || ''} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  ),
}

/**
 * Custom markdown with GDS components
 */
export function GDSMarkdown(props: Props) {
  const { children, updateComponents = c => c } = props

  const components = React.useMemo(() => updateComponents(defaultComponentMap), [updateComponents])

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={components}
      className={classes.gdsMarkdown}
    >
      {children}
    </ReactMarkdown>
  )
}
